import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { getCurrentMultTableFactor } from '~/bootcamp/Activities/Multiplication/MultiplyByA/helpers'
import { randomIntFromInterval, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

export default function MultiplyByUnknown({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  levelId,
  activityType,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef, levelId)

  const [currentMultTableFactor, randomFactor, unknownIsRandomFactor, unknownPos] = useMemo(() => {
    setAnswer('')

    return [
      getCurrentMultTableFactor(levelId, activityType),
      randomIntFromInterval(1, 9, taskHistory),
      Math.random() > 0.33,
      Math.random() > 0.5 ? 'left' : 'right',
    ]
  }, [activityType, levelId, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  // The string on screen, never the tuple above: that names which factor is the unknown.
  const shownFactor = unknownIsRandomFactor ? currentMultTableFactor : randomFactor
  const product = currentMultTableFactor * randomFactor
  useEmitInstance(
    onInstanceChange,
    unknownPos === 'left'
      ? `\\square \\cdot ${shownFactor} = ${product}`
      : `${shownFactor} \\cdot \\square = ${product}`,
  )

  const handleOnCheckAnswerClick = (): void => {
    const knownFactor = unknownIsRandomFactor ? currentMultTableFactor : randomFactor
    const isCorrect = Number(answer) * knownFactor === currentMultTableFactor * randomFactor
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => (prevState.length < 8 ? [randomFactor, ...prevState] : []))
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  const input = (
    <Input
      aria-label={t('ariaLabelButtons.insertProduct')}
      autoFocus={autofocus}
      isError={answerState === false}
      maxDigits={3}
      onChange={(value) => setAnswer(value)}
      onEnter={onEnterPress}
      ref={inputRef}
      value={answer}
    />
  )

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper>
        <span>
          {unknownPos === 'left' ? (
            <Fragment>
              <span key={`${inputKey}-left`}>{input}</span>
              <Times />
              <span>{unknownIsRandomFactor ? currentMultTableFactor : randomFactor}</span>
            </Fragment>
          ) : (
            <Fragment>
              <span>{unknownIsRandomFactor ? currentMultTableFactor : randomFactor}</span>
              <Times />
              <span key={`${inputKey}-right`}>{input}</span>
            </Fragment>
          )}
        </span>
        <span className="block sm:inline-block">
          <Equals />
          <span>{currentMultTableFactor * randomFactor}</span>
        </span>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}
